import React, { useEffect, useState } from 'react';
import '../../styles/CookieConsent.css';

const privacyLink = '/legal#privacy';
const termsLink = '/legal#terms';
const cookieSettingsDoc = '/legal#cookies';

const defaultPrefs = {
  essential: true,
  analytics: false,
  marketing: false,
  functional: false,
};

const CookieConsent = ({ openManager = false, onCloseManager = () => { }, theme = 'dark' }) => {
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [showBanner, setShowBanner] = useState(false);
  const [showManager, setShowManager] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('cookiePreferences');
      if (stored) {
        const parsed = JSON.parse(stored);
        setPrefs({ ...defaultPrefs, ...parsed, essential: true });
        setShowBanner(false);
      } else {
        setShowBanner(true);
      }
    } catch (err) {
      console.error('Failed to load cookie preferences', err);
      setShowBanner(true);
    }
  }, []);

  useEffect(() => {
    if (openManager) {
      setShowManager(true);
      setShowBanner(false);
    }
  }, [openManager]);

  const persistPrefs = (nextPrefs) => {
    const merged = { ...defaultPrefs, ...nextPrefs, essential: true };
    setPrefs(merged);
    localStorage.setItem('cookiePreferences', JSON.stringify(merged));
    setShowBanner(false);
    setShowManager(false);
    onCloseManager();
  };

  const handleAcceptAll = () => {
    persistPrefs({
      analytics: true,
      marketing: true,
      functional: true,
    });
  };

  const handleReject = () => {
    persistPrefs({
      analytics: false,
      marketing: false,
      functional: false,
    });
  };

  const handleSave = () => {
    persistPrefs(prefs);
  };

  const handleCloseManager = () => {
    setShowManager(false);
    if (!localStorage.getItem('cookiePreferences')) {
      setShowBanner(true);
    }
    onCloseManager();
  };

  const togglePref = (key) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderToggle = (key, label, description, disabled = false) => (
    <div className={`cookie-toggle ${disabled ? 'disabled' : ''}`}>
      <div>
        <p className="cookie-toggle__label">{label}</p>
        <p className="cookie-toggle__description">{description}</p>
      </div>
      <label className="cookie-switch">
        <input
          type="checkbox"
          checked={!!prefs[key]}
          disabled={disabled}
          onChange={() => togglePref(key)}
        />
        <span className="cookie-slider" />
      </label>
    </div>
  );

  const bannerClass = `cookie-banner${theme === 'light' ? ' cookie-banner--light' : ''}`;
  const modalClass = `cookie-modal${theme === 'light' ? ' cookie-modal--light' : ''}`;

  return (
    <>
      {showBanner && (
        <div className={bannerClass}>
          <div className="cookie-banner__content">
            <p className="cookie-banner__title">We use cookies</p>
            <p className="cookie-banner__text">
              We use cookies to make the site work and understand how it's used. Essential cookies are always on. Analytics cookies help us improve the site. You can accept all, reject non-essential, or manage preferences.
            </p>
            <div className="cookie-banner__links">
              <a href={privacyLink}>Privacy Policy</a>
              <a href={termsLink}>Terms of Use</a>
              <a href={cookieSettingsDoc}>Cookie Policy</a>
            </div>
          </div>
          <div className="cookie-banner__actions">
            <button type="button" className="cookie-btn ghost" onClick={handleReject}>Reject non-essential</button>
            <button type="button" className="cookie-btn ghost" onClick={() => { setShowManager(true); setShowBanner(false); }}>Manage preferences</button>
            <button type="button" className="cookie-btn primary" onClick={handleAcceptAll}>Accept all</button>
          </div>
        </div>
      )}

      {showManager && (
        <div className="cookie-modal__overlay">
          <div className={modalClass}>
            <div className="cookie-modal__header">
              <div>
                <h3>Manage cookie preferences</h3>
                <p>Adjust how Eclipseer uses cookies. Essential cookies stay on to keep the site running.</p>
              </div>
              <button className="cookie-modal__close" onClick={handleCloseManager} aria-label="Close cookie settings">
                ×
              </button>
            </div>

            <div className="cookie-modal__body">
              <div className="cookie-section">
                <h4>1. About these cookies</h4>
                <p>We use cookies to keep the site secure, remember your preferences, and improve our product through analytics.</p>
              </div>

              <div className="cookie-section">
                <h4>2. Essential cookies</h4>
                <p>Required for security, login, and core functionality. These are always on.</p>
                {renderToggle('essential', 'Essential (always on)', 'Keeps Eclipseer secure and functional.', true)}
              </div>

              <div className="cookie-section">
                <h4>3. Analytics cookies</h4>
                <p>Help us understand usage so we can improve features and reliability.</p>
                {renderToggle('analytics', 'Analytics', 'Anonymous usage insights to improve Eclipseer.')}
              </div>
            </div>

            <div className="cookie-modal__footer">
              <div className="cookie-modal__links">
                <a href={privacyLink}>Privacy Policy</a>
                <a href={termsLink}>Terms of Use</a>
                <a href={cookieSettingsDoc}>Cookie Policy</a>
              </div>
              <div className="cookie-modal__actions">
                <button type="button" className="cookie-btn ghost" onClick={handleReject}>Reject non-essential</button>
                <button type="button" className="cookie-btn ghost" onClick={handleSave}>Save preferences</button>
                <button type="button" className="cookie-btn primary" onClick={handleAcceptAll}>Accept all</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
